import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { ButtonComponent } from 'src/app/_components/buttons/button/button.component';
import { MatFormFieldComponent } from 'src/app/_components/forms/mat-form-field/mat-form-field.component';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    RouterLink,
    MatFormFieldComponent,
    NgOptimizedImage,
  ],
})
export class ForgotPasswordComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly destroyRef = inject(DestroyRef);

  isProcessing = signal(false);
  requestError = signal<ErrorObject | null>(null);

  emailField = new FormBase({
    key: 'email',
    label: 'Email',
    type: 'email',
    placeholder: 'Email Address',
    disabled: false,
    required: true,
    value: '',
    options: [],
    autocomplete: 'email',
  });

  resetForm = this.formBuilder.group({
    [this.emailField.key]: ['', [Validators.required, Validators.email]],
  });

  get f(): FormGroup['controls'] {
    return this.resetForm.controls;
  }

  onSubmit(): void {
    if (this.resetForm.invalid || this.isProcessing()) {
      return;
    }

    this.isProcessing.set(true);
    this.requestError.set(null);

    this.authenticationService
      .forgotPassword(this.f.email.value as string)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isProcessing.set(false)),
      )
      .subscribe({
        next: () => void this.router.navigate(['/reset-sent']),
        error: (error: ErrorObject) => this.requestError.set(error),
      });
  }
}
